import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        glass: "bg-white/20 dark:bg-black/20 backdrop-blur-xl border-2 border-white/40 dark:border-primary/40 text-foreground hover:bg-white/30 dark:hover:bg-black/30 hover:border-white/60 dark:hover:border-primary/60 shadow-[0_0_20px_rgba(255,255,255,0.2)] dark:shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] dark:hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] relative overflow-hidden before:absolute before:inset-0 before:rounded-md before:bg-gradient-to-r before:from-white/15 before:via-white/8 before:to-white/15 dark:before:from-primary/10 dark:before:via-primary/5 dark:before:to-primary/10 before:opacity-50 after:absolute after:inset-[1px] after:rounded-md after:bg-gradient-to-b after:from-white/20 after:via-transparent after:to-white/10 dark:after:from-white/10 dark:after:via-transparent dark:after:to-white/5 after:opacity-50 [&>*]:relative [&>*]:z-10",
        neon: "relative overflow-hidden text-primary-foreground bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--ring))] to-[hsl(var(--primary))] hover:brightness-110 border-2 border-[hsl(var(--primary)_/_0.6)] hover:border-[hsl(var(--primary)_/_0.85)] shadow-[0_0_24px_hsl(var(--primary)_/_0.65)] hover:shadow-[0_0_36px_hsl(var(--primary)_/_0.9)] transition-all before:absolute before:inset-[-20%] before:bg-[radial-gradient(ellipse_at_center,hsl(var(--primary)_/_0.35),transparent_60%)] before:opacity-80 before:pointer-events-none after:absolute after:inset-[1px] after:rounded-md after:bg-gradient-to-b after:from-white/10 after:via-transparent after:to-white/5 [&>*]:relative [&>*]:z-10",
        ctaBlue: "bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-500 hover:from-cyan-500 hover:via-blue-600 hover:to-cyan-600 text-white font-semibold transition-all duration-300",
        ctaBlueThemed: "bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-500 hover:from-cyan-300 hover:via-blue-400 hover:to-cyan-400 text-gray-900 dark:text-white border-2 border-cyan-300/60 shadow-[0_0_25px_rgba(0,255,255,0.8)] hover:shadow-[0_0_35px_rgba(0,255,255,1)] transition-all duration-500 font-semibold",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
